import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody, CardHeader } from '../components/Card.js'
import { Empty } from '../components/Empty.js'
import { Icon } from '../components/Icon.js'
import { FeatureGate } from '../components/FeatureGate.js'
import { UrgencyBadge } from '../components/UrgencyBadge.js'
import { FEATURES } from '@academy/config'

export const Route = createFileRoute('/_app/exam-calendar')({
  component: () => (
    <FeatureGate feature={FEATURES.EXAM_BOARDS}>
      <ExamCalendarPage />
    </FeatureGate>
  ),
})

type Readiness = 'on_track' | 'needs_attention' | 'at_risk'
type Board = 'icse' | 'ssc' | 'cbse' | 'state_board' | 'other'

interface ExamStudent {
  studentId: string
  studentName: string
  classId: string
  className: string
  attendancePct: number | null
  attendanceSample: number
  avgScore: number | null
  avgScoreSample: number
  readiness: Readiness
}

interface ExamRow {
  id: string
  board: Board
  subject: string
  examDate: string
  academicYear: string
  notes: string | null
  daysUntil: number
  students: ExamStudent[]
}

const HORIZONS = [7, 14, 30] as const
type Horizon = (typeof HORIZONS)[number]

function ExamCalendarPage() {
  const [horizon, setHorizon] = useState<Horizon>(14)

  const calendar = useQuery({
    queryKey: ['exam-calendar', horizon],
    queryFn: async () => {
      const res = await api<{ exams: ExamRow[] }>(
        `/api/v1/exam-calendar?days=${horizon}`,
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const exams = calendar.data?.exams ?? []

  const totals = exams.reduce(
    (acc, exam) => {
      for (const s of exam.students) {
        acc[s.readiness] += 1
      }
      return acc
    },
    { on_track: 0, needs_attention: 0, at_risk: 0 } as Record<Readiness, number>,
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Exam calendar"
        description="Upcoming exams for boards your classes sit for, with student preparation status pulled from attendance and recent test scores."
      />

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium uppercase tracking-wide text-fg-subtle">
          Window
        </span>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          {HORIZONS.map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => setHorizon(h)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                horizon === h
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-surface text-fg-muted hover:bg-surface-muted'
              }`}
            >
              Next {h} days
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <ReadinessTile
          label="On track"
          value={totals.on_track}
          tone="success"
        />
        <ReadinessTile
          label="Needs attention"
          value={totals.needs_attention}
          tone="warning"
        />
        <ReadinessTile label="At risk" value={totals.at_risk} tone="danger" />
      </div>

      {calendar.isLoading ? (
        <p className="text-sm text-fg-subtle">Loading…</p>
      ) : exams.length === 0 ? (
        <Card>
          <Empty
            icon="calendar"
            title="No exams in this window"
            description="Either none are scheduled, or none of your classes' boards have exams coming up. Try a longer window."
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {exams.map((exam) => (
            <Card key={exam.id}>
              <CardHeader
                title={
                  <span className="flex flex-wrap items-baseline gap-2">
                    <span>{exam.subject}</span>
                    <span className="text-xs uppercase font-medium text-fg-muted">
                      {exam.board.replace('_', ' ')}
                    </span>
                  </span>
                }
                description={
                  <span className="flex flex-wrap items-center gap-3">
                    <span>
                      {new Date(exam.examDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                    <UrgencyBadge daysUntil={exam.daysUntil} />
                    <span className="text-fg-subtle">
                      · {exam.students.length} student
                      {exam.students.length === 1 ? '' : 's'}
                    </span>
                  </span>
                }
              />
              {exam.notes ? (
                <CardBody className="pt-0 text-sm text-fg-muted whitespace-pre-wrap">
                  {exam.notes}
                </CardBody>
              ) : null}

              {exam.students.length === 0 ? (
                <Empty
                  icon="users"
                  title="No matching students in your classes"
                />
              ) : (
                <ul className="divide-y divide-border">
                  {exam.students.map((s) => (
                    <li
                      key={s.studentId}
                      className="px-5 py-3 flex flex-wrap items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-fg">{s.studentName}</p>
                        <p className="text-xs text-fg-subtle">{s.className}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <ScoreCell
                          label="Subject avg"
                          value={s.avgScore !== null ? `${s.avgScore}%` : '—'}
                          sample={s.avgScoreSample}
                        />
                        <ScoreCell
                          label="Attendance"
                          value={
                            s.attendancePct !== null ? `${s.attendancePct}%` : '—'
                          }
                          sample={s.attendanceSample}
                        />
                        <ReadinessPill readiness={s.readiness} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

function ReadinessTile({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'success' | 'warning' | 'danger'
}) {
  const cls =
    tone === 'success'
      ? 'border-success-border bg-success-tint text-success'
      : tone === 'warning'
        ? 'border-warning-border bg-warning-tint text-warning'
        : 'border-danger-border bg-danger-tint text-danger'
  return (
    <div className={`rounded-lg border px-4 py-3 ${cls}`}>
      <p className="text-xs uppercase tracking-wide opacity-80">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  )
}


function ReadinessPill({ readiness }: { readiness: Readiness }) {
  const map: Record<Readiness, { label: string; cls: string }> = {
    on_track: {
      label: 'On track',
      cls: 'bg-success-tint text-success border-success-border',
    },
    needs_attention: {
      label: 'Needs attention',
      cls: 'bg-warning-tint text-warning border-warning-border',
    },
    at_risk: {
      label: 'At risk',
      cls: 'bg-danger-tint text-danger border-danger-border',
    },
  }
  const it = map[readiness]
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${it.cls}`}
    >
      {it.label}
    </span>
  )
}

function ScoreCell({
  label,
  value,
  sample,
}: {
  label: string
  value: string
  sample: number
}) {
  return (
    <div className="text-right">
      <p className="text-[10px] uppercase tracking-wide text-fg-subtle">{label}</p>
      <p className="text-sm font-semibold text-fg tabular-nums">{value}</p>
      {sample === 0 ? (
        <p className="text-[10px] text-fg-faint inline-flex items-center gap-0.5">
          <Icon name="alert" size={10} /> no data
        </p>
      ) : null}
    </div>
  )
}
